/**
 * Смена сессии и пользователя (CC §4.11, CLIENT_WEB.md §2.4 п. 5).
 *
 * Ключевое различие:
 *  - 401 `session_expired` — локальные данные, cursor и очередь pending
 *    СОХРАНЯЮТСЯ до определения `Me.user.id` новой сессии;
 *  - явный выход и смена владельца сессии — база удаляется целиком.
 */
import { api } from '@shared/api/client'
import { clearActiveUser, deleteUserDb, draftsRepo, getActiveUserId, closeUserDb } from '@shared/db'
import { dropPushSubscription } from '@shared/push'
import { setSyncEngine, syncEngine } from '@shared/sync/instance'
import { postTabMessage, releaseLeadership } from '@shared/ws/leader'

import { useDiscoveryStore } from '@features/discovery/discovery-store'

import { useAppStore } from '../model/app-store'
import { clearScreenScroll } from '../model/screen-scroll'
import { clearScrollMemory } from '../model/scroll-memory'
import { stopMeMirror } from './me-mirror'

/** 401: показываем SYS-07, данные не трогаем. */
export async function handleSessionLost(): Promise<void> {
  const store = useAppStore.getState()

  // Черновики уже durable (Dexie) — отдельного сохранения не требуется,
  // но открытые поля ввода успевают записаться перед показом экрана.
  await flushDrafts()

  syncEngine()?.stop()
  store.setBlocking({ kind: 'session_expired' })
}

async function flushDrafts(): Promise<void> {
  try {
    await draftsRepo.all()
  } catch {
    /* база могла быть уже закрыта */
  }
}

const LOGOUT_STEP_TIMEOUT_MS = 2_000
const LOGOUT_STEP_TIMED_OUT = Symbol('logout step timed out')

async function waitForLogoutStep<T>(task: () => T | Promise<T>): Promise<T | typeof LOGOUT_STEP_TIMED_OUT> {
  let timer: ReturnType<typeof setTimeout> | undefined
  const deadline = new Promise<typeof LOGOUT_STEP_TIMED_OUT>((resolve) => {
    timer = setTimeout(() => resolve(LOGOUT_STEP_TIMED_OUT), LOGOUT_STEP_TIMEOUT_MS)
  })

  try {
    return await Promise.race([Promise.resolve().then(task), deadline])
  } finally {
    clearTimeout(timer)
  }
}

async function bestEffort(task: () => unknown | Promise<unknown>): Promise<void> {
  try {
    await task()
  } catch {
    /* Каждый шаг очистки независим и не должен блокировать следующий. */
  }
}

async function bestEffortWithDeadline(task: () => unknown | Promise<unknown>): Promise<void> {
  try {
    await waitForLogoutStep(task)
  } catch {
    /* Deadline защищает UI от зависшей операции хранилища. */
  }
}

/** Явный выход (S-24): удаляем локальные данные безусловно. */
export async function logout(): Promise<void> {
  const store = useAppStore.getState()
  let userId = store.me?.user.id ?? null

  if (!userId) {
    try {
      const storedUserId = await waitForLogoutStep(getActiveUserId)
      if (storedUserId !== LOGOUT_STEP_TIMED_OUT) userId = storedUserId
    } catch {
      /* store уже содержит владельца при штатном выходе */
    }
  }

  /*
   * §8.2 п. 6: подписка удаляется ДО выхода — после `POST /auth/logout`
   * сессии уже нет и `DELETE /push/subscriptions/{id}` ответит 401.
   * Ошибка локального Push/IndexedDB не отменяет сам выход.
   */
  await bestEffort(dropPushSubscription)
  await bestEffort(() => api.POST('/auth/logout'))

  await bestEffort(stopMeMirror)
  await bestEffort(clearScrollMemory)
  await bestEffort(clearScreenScroll)
  await bestEffort(() => useDiscoveryStore.getState().reset())
  await bestEffort(() => syncEngine()?.stop())
  await bestEffort(() => setSyncEngine(null))

  // §2.4 п. 5: сначала сообщаем вкладкам причину, затем удаляем базу.
  await bestEffort(() => postTabMessage({ type: 'logout' }))
  await bestEffort(closeUserDb)

  if (userId) {
    await bestEffort(async () => {
      const result = await deleteUserDb(userId)
      // Если удаление заблокировано замороженной вкладкой — просим перезагрузку.
      if (!result.deleted && result.blocked) {
        await bestEffort(() => postTabMessage({ type: 'force_reload' }))
      }
    })
  }

  /* Readwrite-транзакция поставлена в очередь до возможного нового входа. */
  await bestEffortWithDeadline(clearActiveUser)
  await bestEffort(clearMediaCache)
  await bestEffort(releaseLeadership)
  await bestEffort(() => store.reset())
}

/** Оба действия выполняются один раз, а вызывающий никогда не получает необработанный rejection. */
export async function settleLogoutFlow(
  logoutAction: () => void | Promise<void>,
  navigateToStart: () => void | Promise<void>,
): Promise<void> {
  try {
    await logoutAction()
  } catch {
    /* локальная навигация всё равно должна выполниться */
  }

  try {
    await navigateToStart()
  } catch {
    /* вызывающий намеренно запускает поток без ожидания; rejection уже обработан */
  }
}

export function logoutAndNavigate(navigateToStart: () => void | Promise<void>): Promise<void> {
  return settleLogoutFlow(logout, navigateToStart)
}

/** §9.3 п. 8: при выходе и смене пользователя кэш медиа удаляется. */
async function clearMediaCache(): Promise<void> {
  navigator.serviceWorker?.controller?.postMessage({ type: 'MEDIA_CLEAR' })
}

/**
 * Смена владельца сессии (CC §4.11 п. 2): вошли под другим `Me.user.id`.
 * База предыдущего пользователя удаляется целиком, без выборочной чистки.
 */
export async function handleOwnerChange(previousUserId: string, nextUserId: string): Promise<void> {
  postTabMessage({ type: 'owner_changed', user_id: nextUserId })

  stopMeMirror()
  clearScrollMemory()
  clearScreenScroll()
  useDiscoveryStore.getState().reset()
  syncEngine()?.stop()
  setSyncEngine(null)
  closeUserDb()

  await deleteUserDb(previousUserId)
  await clearMediaCache()
}
